import { create } from 'zustand';
import { apiUrl } from '../../core/api/api-url';
import { appClient } from '../../core/api/http';
import { toApiError, type ApiError } from '../../core/api/api-error';
import { createQuietRefresh } from '../../core/reach/quiet-refresh';
import {
  restoreLastCleared, shownFromStorageNow, useShownFromStorage, type StoredViewSummary,
} from '../../core/stored-views/presentation.store';
import { useWritable } from '../../core/stored-views/use-writable';
import type { TagSummary } from '../../core/api/types';

/**
 * The tags page's state — the cloud (FR-025) and the three management writes (FR-026 to FR-028).
 *
 * Separate from `tag.store.ts`, which is the tag *view*. They share a noun and nothing else: that
 * one holds actions for one tag and reuses the container machinery; this one holds a list of tags
 * and no actions at all. Merging them would give a store whose fields are meaningful in disjoint
 * halves, which is the shape `TagData`'s own header argues against.
 *
 * Nothing here is optimistic. `optimistic.ts` exists for a star or a completion — a cheap, obvious,
 * reversible change to one row. A merge rewrites taggings across every action carrying two tags and
 * cannot be undone; showing it as done before the server has agreed would mean showing a lie in the
 * one place this feature can least afford one.
 */

/** The offer a colliding rename turns into: merge THIS tag into the one already holding the name. */
export interface MergeSuggestion {
  readonly sourceId: number;
  readonly targetId: number;
}

export type TagsStatus =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready' }
  | { readonly kind: 'error'; readonly error: ApiError };

interface TagsState {
  readonly status: TagsStatus;
  /** In the server's order, never re-sorted (006 FR-017). */
  readonly tags: readonly TagSummary[];
  /**
   * 036 (issue 221, FR-020) — whether ANY load has ever succeeded in this store's lifetime.
   * `noStoredCopy` on `tags-page.tsx` reads this the way every other screen reads `status.data ===
   * null`: this store's `TagsStatus` carries no data of its own (`tags` lives beside it), so this is
   * the one thing that tells a genuine first-load failure from a later refresh failing with a cloud
   * already on screen.
   */
  readonly loadedOnce: boolean;
  /** The last refused write. Separate from `status`: a refusal must not blank the cloud. */
  readonly problem: ApiError | null;
  readonly mergeSuggestion: MergeSuggestion | null;
  /** Tag ids with a write in flight, so two tags never wait on each other. */
  readonly pending: readonly number[];
}

const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

const INITIAL: TagsState = {
  status: { kind: 'idle' },
  tags: [],
  loadedOnce: false,
  problem: null,
  mergeSuggestion: null,
  pending: [],
};

const store = create<TagsState>()(() => INITIAL);

// --- reading -----------------------------------------------------------------------------------

/**
 * The ends of the practitioner's OWN range, which is what the cloud scales against (R-010).
 * Both fall back to 1 on an empty cloud rather than `Infinity` — `Math.min()` of nothing is
 * `Infinity`, and it reaches a `calc()` the browser then silently discards.
 */
const smallestOf = (tags: readonly TagSummary[]): number =>
  (tags.length === 0 ? 1 : Math.min(...tags.map((t) => t.actionCount)));
const largestOf = (tags: readonly TagSummary[]): number =>
  (tags.length === 0 ? 1 : Math.max(...tags.map((t) => t.actionCount)));

export const tagsStatus = {
  use: (): TagsStatus => store((s) => s.status),
  get: (): TagsStatus => store.getState().status,
};
export const tagsList = {
  use: (): readonly TagSummary[] => store((s) => s.tags),
  get: (): readonly TagSummary[] => store.getState().tags,
};
export const tagsLoadedOnce = {
  use: (): boolean => store((s) => s.loadedOnce),
  get: (): boolean => store.getState().loadedOnce,
};
export const tagsProblem = {
  use: (): ApiError | null => store((s) => s.problem),
  get: (): ApiError | null => store.getState().problem,
};
export const tagsMergeSuggestion = {
  use: (): MergeSuggestion | null => store((s) => s.mergeSuggestion),
  get: (): MergeSuggestion | null => store.getState().mergeSuggestion,
};
export const tagsSmallest = {
  use: (): number => smallestOf(store((s) => s.tags)),
  get: (): number => smallestOf(store.getState().tags),
};
export const tagsLargest = {
  use: (): number => largestOf(store((s) => s.tags)),
  get: (): number => largestOf(store.getState().tags),
};

/** True while this tag has a write in flight — per id, so two tags never wait on each other. */
export const isTagPending = (tagId: number): boolean => store.getState().pending.includes(tagId);

// --- writing -----------------------------------------------------------------------------------

const tagsUrl = (): string => apiUrl('/api/v1/tags');

/** 036 (issue 221, FR-021): this screen's one read. */
export function tagsListViewUrls(): readonly string[] {
  return [tagsUrl()];
}

/** FR-018/FR-024 — whether this screen's one read is currently drawn from storage. */
export const tagsListStoredView = {
  use: (): StoredViewSummary => useShownFromStorage(tagsListViewUrls()),
  get: (): StoredViewSummary => shownFromStorageNow(tagsListViewUrls()),
};

/** FR-035, research R-022 — false while a stored view is shown. Rename, merge and delete are all
 *  absent under this: none of them is named by FR-026. */
export const useTagsListWritable = (): boolean => useWritable(tagsListViewUrls());

const begin = (id: number): void =>
  store.setState({ pending: [...store.getState().pending, id], problem: null, mergeSuggestion: null });
const release = (id: number): void => {
  store.setState({ pending: store.getState().pending.filter((p) => p !== id) });
  quietRefresh.settled();
};

/**
 * FR-026's refusal, turned into the next step rather than repeated.
 *
 * The API is right to answer 409 — a rename is not a merge and it must never silently become
 * one. But "that name is taken" leaves the practitioner holding two tags they wanted to be one
 * and nothing to click. So the conflict becomes a sentence naming the tag in the way plus a
 * `mergeSuggestion` the page can offer as an operation. The `code` is kept as the server sent
 * it: this rewrites the wording, not the diagnosis.
 */
const asMergeOffer = (problem: ApiError, sourceId: number, attempted: string): ApiError => {
  const target = store.getState().tags.find((t) => same(t.name, attempted) && t.id !== sourceId);
  if (problem.code !== 'tag.name_taken' || !target) return problem;

  store.setState({ mergeSuggestion: { sourceId, targetId: target.id } });
  return {
    ...problem,
    detail: `You already have a tag called “${target.name}”. Merge this one into it instead — every action carrying it will move across.`,
  };
};

/** The ONE place this request is written; `loadTags()` and `quietRefreshTags()` both call this
 *  rather than building it by hand. */
const readTags = (): Promise<TagSummary[]> => appClient().get<TagSummary[]>(tagsUrl());

/** `core/reach/quiet-refresh.ts` — see that file for the mechanism. */
const quietRefresh = createQuietRefresh<TagSummary[]>({
  ready: () => store.getState().status.kind === 'ready',
  busy: () => store.getState().pending.length > 0,
  read: readTags,
  apply: (tags) => store.setState({ tags }),
  busyDiscard: () => restoreLastCleared(tagsListViewUrls()),
});

/** Every local change to the cloud goes through this, so the quiet refresh always knows one
 *  happened — a rename, a delete or a merge landing. */
const applyTags = (tags: readonly TagSummary[]): void => {
  quietRefresh.touched();
  store.setState({ tags });
};

/**
 * FR-025: the whole vocabulary, so no `term`. That parameter is what makes this same route the
 * autocomplete, which caps at ten — a cloud missing its eleventh tag lies about the vocabulary.
 */
export async function loadTags(): Promise<void> {
  // A fresh load supersedes anything a quiet refresh has in flight.
  quietRefresh.touched();
  store.setState({ status: { kind: 'loading' }, problem: null, mergeSuggestion: null });
  try {
    const tags = await readTags();
    store.setState({ status: { kind: 'ready' }, tags, loadedOnce: true });
  } catch (error) {
    // 036 (issue 221): `tags`/`loadedOnce` are left UNTOUCHED — a failed reload must not blank a
    // cloud the practitioner was reading (the same carry-forward every other screen's `status.data`
    // already gives it), and `loadedOnce` staying whatever it was is what tells `tags-page.tsx`'s
    // `noStoredCopy` a genuine first-load failure from a later one.
    store.setState({ status: { kind: 'error', error: toApiError(error) } });
  }
}

/**
 * The QUIET re-read `resync` (FR-036) must trigger, not `loadTags()` itself. `loadTags()` flips
 * `status` to `loading` while it runs; nothing here gates a write on `status` being `ready`
 * (`renameTag`/`removeTag`/`mergeTags` all call the API directly), but a rename, merge or delete made
 * while the CLOUD is mid-refresh could still be raced by the refresh's own answer overwriting a
 * result the write already folded in. Re-reads the same one request and replaces `tags` only once it
 * has answered and nothing has moved since, never touching `status`, `pending` or `problem`.
 */
export const quietRefreshTags = quietRefresh.run;

/** FR-026. The renamed tag replaces its old self in place — the order is still the server's. */
export async function renameTag(tagId: number, name: string): Promise<void> {
  begin(tagId);
  try {
    const renamed = await appClient().patch<TagSummary>(`${tagsUrl()}/${tagId}`, { name });
    applyTags(store.getState().tags.map((t) => (t.id === tagId ? renamed : t)));
  } catch (error) {
    store.setState({ problem: asMergeOffer(toApiError(error), tagId, name) });
  } finally {
    release(tagId);
  }
}

/**
 * FR-027. The count the practitioner was shown before confirming came from this list, and the
 * delete returns no second number to disagree with it (research R-012).
 */
export async function removeTag(tagId: number): Promise<void> {
  begin(tagId);
  try {
    await appClient().delete<void>(`${tagsUrl()}/${tagId}`);
    applyTags(store.getState().tags.filter((t) => t.id !== tagId));
  } catch (error) {
    store.setState({ problem: toApiError(error) });
  } finally {
    release(tagId);
  }
}

/**
 * FR-028. The survivor's count is the SERVER's, never `source + target`: an action carrying
 * both ends with one tagging, so adding the two would over-count by exactly the overlap
 * (SC-004a) — and the overlap is the case a practitioner merges to fix.
 */
export async function mergeTags(sourceId: number, targetId: number): Promise<void> {
  begin(sourceId);
  try {
    const survivor = await appClient().post<TagSummary>(`${tagsUrl()}/${sourceId}/merge`, { intoTagId: targetId });
    applyTags(store.getState().tags.filter((t) => t.id !== sourceId).map((t) => (t.id === survivor.id ? survivor : t)));
  } catch (error) {
    store.setState({ problem: toApiError(error) });
  } finally {
    release(sourceId);
  }
}

/** Dismiss a refusal without reloading — the cloud behind it never went stale. */
export function clearTagsProblem(): void {
  store.setState({ problem: null, mergeSuggestion: null });
}

/** Test seam, like the other stores'. Production code never calls it. */
export function resetTags(): void {
  store.setState(INITIAL);
  quietRefresh.reset();
}
